import { useState } from 'react'
import type { InspectorResource, StateFinding, StateVersionSummary } from '@/lib/apiTypes'
import { useStateResources } from '@/lib/hooks'
import { api } from '@/lib/api'
import { useToast } from '@/context/ToastContext'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonTable } from '@/shared/components/Skeleton'
import { FeedbackMessage } from '@/shared/components/FeedbackMessage'
import { Modal } from '@/shared/components/Modal'
import { ConfirmModal } from '@/shared/components/ConfirmModal'
import { IconButton } from '@/shared/components/IconButton'
import { TrashIcon } from '@/shared/components/Icons'
import { ResourceDetail } from '@/features/state/components/ResourceDetail'
import { CHANGE_STYLE, moduleDepth, resourceFindings } from '@/features/state/lib/inspector'

export interface ResourceInspectorProps {
  orgId: string
  workspace: string
  versions: StateVersionSummary[]
  selectedVersionId: string
  lockActive: boolean
  findings: StateFinding[]
  onViewFindings: () => void
  onMutated: () => void
  /** address -> plan replacement cause ("# forces replacement" attribute), when reused from a
   *  plan detail. Renders inline on the matching resource. Omitted for the state-page usage. */
  replacementCauses?: Record<string, string>
}

/** Standalone modal wrapper (Plans.tsx's launcher-button usage). The State workspace detail
 *  modal instead renders {@link ResourceInspectorPanel} directly as a tab, with no nested dialog. */
export function ResourceInspector(props: ResourceInspectorProps & { onClose: () => void }) {
  const { onClose, ...panelProps } = props
  const selectedSerial = props.versions.find((v) => v.id === props.selectedVersionId)?.serial ?? null
  return (
    <Modal track="state_resource_inspector" title="Resource Inspector" kicker={`// ${props.workspace} · serial ${selectedSerial ?? '?'}`} onClose={onClose} maxWidth={1100}>
      <ResourceInspectorPanel {...panelProps} />
    </Modal>
  )
}

export function ResourceInspectorPanel({
  orgId, workspace, versions, selectedVersionId, lockActive, findings, onViewFindings, onMutated,
  replacementCauses,
}: ResourceInspectorProps) {
  const { showToast } = useToast()
  const selectedIdx = versions.findIndex((v) => v.id === selectedVersionId)
  const selectedSerial = versions[selectedIdx]?.serial ?? null
  const previousVersion = selectedIdx >= 0 ? versions[selectedIdx + 1] ?? null : null

  const [compareMode, setCompareMode] = useState(false)
  const resources = useStateResources(orgId, workspace, selectedVersionId, compareMode ? previousVersion?.id ?? null : null)

  const [search, setSearch] = useState('')
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [multi, setMulti] = useState<Set<string>>(new Set())
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const allResources = resources.data?.resources ?? []
  const filtered = search.trim()
    ? allResources.filter((r) => r.address.toLowerCase().includes(search.trim().toLowerCase()) || r.type.toLowerCase().includes(search.trim().toLowerCase()))
    : allResources

  // Group by module path for the tree.
  const groups = new Map<string, InspectorResource[]>()
  for (const r of filtered) {
    const arr = groups.get(r.module) ?? []
    arr.push(r)
    groups.set(r.module, arr)
  }
  const moduleKeys = [...groups.keys()].sort((a, b) => a.localeCompare(b))

  const selectedResource = allResources.find((r) => r.address === selectedAddress) ?? null

  function toggleModule(m: string) {
    setCollapsed((prev) => { const n = new Set(prev); n.has(m) ? n.delete(m) : n.add(m); return n })
  }
  function toggleMulti(addr: string) {
    setMulti((prev) => { const n = new Set(prev); n.has(addr) ? n.delete(addr) : n.add(addr); return n })
  }

  async function handleDelete() {
    setDeleting(true)
    try {
      const res = await api.post<{ serial: number; removed: number | null }>(
        `/api/v1/${orgId}/state/${encodeURIComponent(workspace)}/versions/${selectedVersionId}/resources/delete`,
        // A fresh idempotency key per confirmed removal makes a retried request safe (returns
        // the already-created version rather than a spurious conflict).
        { addresses: [...multi], idempotency_key: crypto.randomUUID() },
      )
      setConfirmDelete(false)
      setMulti(new Set())
      // `removed` is null on an idempotent replay (the first submit already applied it).
      const msg = res.removed == null
        ? `Already applied → serial ${res.serial}.`
        : `Removed ${res.removed} resource${res.removed !== 1 ? 's' : ''} → new serial ${res.serial}.`
      showToast(msg, 'success')
      onMutated()
    } catch (err) {
      const msg = (err as Error).message
      if (/locked|423/i.test(msg)) showToast('Workspace is locked — release the lock and retry.', 'error')
      else if (/serial_conflict|409/i.test(msg)) showToast('State changed — refresh and retry.', 'error')
      else showToast(msg, 'error')
    } finally {
      setDeleting(false)
    }
  }

  const removeDisabledReason = lockActive
    ? 'Workspace is locked'
    : multi.size === 0
      ? 'Select resources to remove'
      : null

  return (
    <div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--text3)', letterSpacing: '0.08em', marginBottom: 10 }}>
        // {workspace} · serial {selectedSerial ?? '?'}
      </div>
      {confirmDelete && (
        <ConfirmModal
          track="state_resource_remove_confirm"
          title="Remove resources from state"
          confirmLabel={`Remove ${multi.size}`}
          danger
          busy={deleting}
          message={
            <>
              Remove <strong>{multi.size}</strong> resource{multi.size !== 1 ? 's' : ''} from this state version? This writes a
              new serial with {multi.size === 1 ? 'it' : 'them'} deleted — the underlying infrastructure is <strong>not</strong> destroyed,
              only forgotten by Terraform. Reversible by promoting an earlier serial.
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 160, overflow: 'auto' }}>
                {[...multi].map((a) => <code key={a} style={{ fontSize: '0.7rem', color: 'var(--text2)' }}>{a}</code>)}
              </div>
            </>
          }
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by address or type…"
          style={{ flex: 1, minWidth: 200, padding: '6px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: '0.72rem' }}
        />
        {resources.data && (
          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--text3)' }}>
            {resources.data.resource_count} resources · {resources.data.sensitive_count} sensitive
          </span>
        )}
        <IconButton
          label="Diff vs previous"
          tip={`Compare against serial ${previousVersion?.serial}`}
          disabledReason={previousVersion ? null : 'No earlier version to compare'}
          active={compareMode}
          onClick={() => setCompareMode((v) => !v)}
        >
          <span style={{ fontSize: '0.65rem' }}>{compareMode ? `Diffing vs ${previousVersion?.serial}` : 'Diff vs previous'}</span>
        </IconButton>
        <IconButton
          label={`Remove ${multi.size} selected`}
          tip={`Remove ${multi.size} selected`}
          disabledReason={removeDisabledReason}
          tint="var(--red)"
          onClick={() => setConfirmDelete(true)}
        >
          <TrashIcon size={13} />
          {multi.size > 0 && <span style={{ fontSize: '0.65rem', marginLeft: 4 }}>({multi.size})</span>}
        </IconButton>
      </div>

      {resources.loading && <SkeletonTable headers={['Resource']} rows={6} firstColTwoLines={false} />}
      {resources.error && <FeedbackMessage ok={false} msg={resources.error} />}
      {!resources.loading && !resources.error && allResources.length === 0 && (
        <EmptyState icon="◈" title="No resources in this state version." />
      )}

      {!resources.loading && allResources.length > 0 && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', minHeight: 300 }}>
          {/* Tree */}
          <div style={{ minWidth: 300, maxWidth: 380, flexShrink: 0, maxHeight: 520, overflow: 'auto' }}>
            {moduleKeys.map((m) => {
              const isCollapsed = collapsed.has(m)
              const depth = moduleDepth(m)
              return (
                <div key={m || '(root)'}>
                  <button
                    onClick={() => toggleModule(m)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left',
                      background: 'transparent', border: 'none', cursor: 'pointer', padding: '5px 4px',
                      paddingLeft: 4 + depth * 12, color: 'var(--text2)', fontFamily: 'var(--mono)', fontSize: '0.66rem',
                    }}
                  >
                    <span style={{ color: 'var(--text3)', width: 10 }}>{isCollapsed ? '▸' : '▾'}</span>
                    <span style={{ color: 'var(--accent)' }}>{m === '' ? 'root' : m.split('.module.').slice(-1)[0].replace(/^module\./, 'module.')}</span>
                    <span className="dim">({groups.get(m)!.length})</span>
                  </button>
                  {!isCollapsed && groups.get(m)!.map((r) => {
                    const rf = resourceFindings(r, findings)
                    const selected = r.address === selectedAddress
                    return (
                      <div
                        key={r.address}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 16 + depth * 12, background: selected ? 'var(--accent-d)' : 'transparent', borderRadius: 4 }}
                      >
                        <input
                          type="checkbox"
                          checked={multi.has(r.address)}
                          onChange={() => toggleMulti(r.address)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={r.change === 'removed'}
                          title={r.change === 'removed' ? 'Already removed in this diff' : 'Select for removal'}
                          style={{ flexShrink: 0 }}
                        />
                        <button
                          onClick={() => setSelectedAddress(r.address)}
                          style={{ flex: 1, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 2px', color: selected ? 'var(--accent)' : 'var(--text2)', fontFamily: 'var(--mono)', fontSize: '0.68rem', minWidth: 0 }}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                            <span>{r.mode === 'data' ? 'data.' : ''}{r.type}.{r.name}</span>
                            {r.instances.length > 1 && <span className="dim">×{r.instances.length}</span>}
                            {compareMode && r.change && r.change !== 'unchanged' && (
                              <span style={{ color: CHANGE_STYLE[r.change].color, fontSize: '0.6rem' }}>●</span>
                            )}
                            {rf.length > 0 && (
                              <span className={`badge badge-${rf.some((f) => f.severity === 'critical') ? 'red' : 'amber'}`} style={{ fontSize: '0.52rem' }}>{rf.length}</span>
                            )}
                          </span>
                        </button>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>

          {/* Detail */}
          <div style={{ flex: 1, minWidth: 0, borderLeft: '1px solid var(--border)', paddingLeft: 16 }}>
            {selectedResource
              ? <ResourceDetail res={selectedResource} findings={findings} onViewFindings={onViewFindings} compareMode={compareMode} replacementCause={replacementCauses?.[selectedResource.address]} />
              : <EmptyState icon="◈" title="Select a resource" subtitle="Pick a resource from the tree to inspect its attributes." />}
          </div>
        </div>
      )}

      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--text3)', marginTop: 14, lineHeight: 1.6 }}>
        // Sensitive values are redacted server-side (Terraform-marked, known secret key names, and
        common credential patterns) and never sent to the browser. A bespoke secret embedded in an
        otherwise plain free-text field may not be detected — treat this as a convenience view, not a
        secret-leak audit.
      </div>
    </div>
  )
}
